import React, { useRef, useState, useEffect } from 'react';
import { palettes, RegularTitle, Theme } from '@indigo-ag/component-module';
import i18next from 'core/i18n';
import { v4 as uuidv4 } from 'uuid';

import { createStylesHook } from 'shared/hooks/createStylesHook';
import { ProgressBar } from 'shared';
import { UploadDocumentItem } from './UploadDocumentItem';
import { ProgressDictionary } from 'features/brazil-grower/components/BrazilGrowerFinance/utils';

interface UploadComponentProps {
  files: File[];
  multipleFiles?: boolean;
  setFiles?: (files: File[]) => void;
  onRemoveFile?: (fileIdx: number, file: File) => void;
  onNewFiles?: (files: File[]) => void;
  progress?: ProgressDictionary;
  replaceItem?: (file: File) => void;
  onUpdateFile?: (file: File) => void;
}

export const useStyles = createStylesHook((theme: Theme) => ({
  selectFilesDropbox: {
    alignContent: 'center',
    alignItems: 'center',
    border: `1px dashed ${palettes.coastal.core.mediumBlue}`,
    borderRadius: 12,
    color: palettes.neutral[700],
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 400,
    height: 100,
    justifyContent: 'center',
    margin: '0 0 25px',
    padding: '4rem 0',
    textAlign: 'center'
  },

  textHandler: {
    color: palettes.coastal.core.mediumBlue,
    cursor: 'pointer'
  },

  wrapper: {
    height: '70%',
    maxWidth: 480
  },

  noDisplay: {
    display: 'none'
  },

  containerDocument: {
    textDecoration: 'underline',
    cursor: 'pointer',
    padding: '0 0 0 5px'
  },

  progressContainer: {
    border: '1px solid #ACC0CA',
    marginBottom: 16
  }
}));

export const UploadDocument: React.FunctionComponent<UploadComponentProps> = ({
  files,
  multipleFiles = true,
  setFiles,
  onRemoveFile,
  onNewFiles,
  progress,
  replaceItem,
  onUpdateFile
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  /**
   * State
   */
  const [loading, setLoading] = useState<boolean>(false);
  /**
   * Styles
   */
  const classes = useStyles();

  useEffect(() => {
    setLoading(false);
  }, []);

  const handleRemoveFile = (fileToRemove: File, fileIdx: number) => {
    return () => {
      if (onRemoveFile) {
        onRemoveFile(fileIdx, fileToRemove);
      }

      const newFiles = files.filter((fileData: any) => fileData !== fileToRemove);

      if (setFiles) {
        setFiles(newFiles);
      }
    };
  };

  const onChooseFile = (): void => {
    (fileInputRef as React.MutableRefObject<HTMLInputElement>).current.click();
    setLoading(true);
  };

  const onFileDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    const fileData: File[] = Array.from(event.dataTransfer.files);
    event.preventDefault();
    event.stopPropagation();

    notifyFilesChanges(fileData);

    (fileInputRef as React.MutableRefObject<HTMLInputElement>).current.value = '';
    setLoading(true);
  };

  const onDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.stopPropagation();
    event.preventDefault();
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const fileData: File[] = Array.from(event.target.files || []);

    notifyFilesChanges(fileData);

    (fileInputRef as React.MutableRefObject<HTMLInputElement>).current.value = '';
  };

  const RenderSingleFileUpload = (): JSX.Element | null => {
    if (multipleFiles || (multipleFiles === false && files.length === 0)) {
      return (
        <div onClick={onChooseFile} className={classes.selectFilesDropbox} onDrop={onFileDrop} onDragOver={onDragOver}>
          {i18next.t('dragFiles')} <br />
          {i18next.t('or')} <br />
          <span className={classes.textHandler}>{i18next.t('browseFileMessage')}</span>
        </div>
      );
    }
    return null;
  };

  const notifyFilesChanges = (newFiles: File[]) => {
    if (newFiles) {
      const formattedNewFiles = newFiles.map(newFile => {
        newFile['isNew'] = true;
        newFile['newFileId'] = uuidv4();
        return newFile;
      });
      if (setFiles) {
        setFiles([...files, ...formattedNewFiles]);
      }
      if (onNewFiles) {
        onNewFiles(formattedNewFiles);
      }
    } else {
      alert(i18next.t('errorUploading'));
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="*"
        multiple={multipleFiles}
        onChange={handleFileUpload}
        className={classes.noDisplay}
      />
      {RenderSingleFileUpload()}
      {files.length > 0 &&
        files.map((fileData, i) => {
          if (loading && progress && progress[fileData['newFileId']] > 0) {
            return (
              <div className={classes.progressContainer} key={i + 'RT'}>
                <RegularTitle style={{ padding: '16px 16px 0px' }}>{fileData.name}</RegularTitle>
                <div>
                  {progress && progress[fileData['newFileId']] >= 0 && (
                    <ProgressBar value={Number(progress[fileData['newFileId']])} />
                  )}
                </div>
              </div>
            );
          }
          return (
            <div key={i + 'UDI'}>
              <UploadDocumentItem
                removeFileAction={handleRemoveFile(fileData, i)}
                file={fileData}
                replaceItem={replaceItem}
                onUpdateFile={onUpdateFile}
              />
            </div>
          );
        })}
    </>
  );
};
